/**
 * @fileoverview File Upload Error Display Component
 * 
 * Displays upload errors with retry options and user-friendly
 * error messages with actionable buttons.
 * 
 * Requirements: 1.5, 1.6, 1.7
 */

import React, { useMemo } from 'react';
import { useModuleContext } from '../../context/ModuleContextProvider.jsx';

/**
 * File Upload Error Display Component
 * 
 * @param {Object} props - Component props
 * @param {Array} props.errors - Array of error objects
 * @param {function} props.onRetry - Retry handler function
 * @param {function} props.onDismiss - Dismiss error handler function
 * @param {string} [props.testId] - Test ID
 */
export const FileUploadErrorDisplay = ({ 
  errors, 
  onRetry, 
  onDismiss, 
  testId = 'file-upload-errors' 
}) => {
  const { theme } = useModuleContext();
  const isDark = theme?.mode === 'dark';

  // Group errors by type for better display
  const groupedErrors = useMemo(() => {
    const groups = {};
    
    errors.forEach(error => {
      const errorType = getErrorType(error.error);
      if (!groups[errorType]) {
        groups[errorType] = {
          type: errorType,
          errors: [],
          retryable: false,
          icon: getErrorIcon(errorType),
          color: getErrorColor(errorType)
        };
      }
      
      groups[errorType].errors.push(error);
      if (error.retryable) {
        groups[errorType].retryable = true;
      }
    });
    
    return Object.values(groups);
  }, [errors]);

  /**
   * Get error type from error message
   */
  function getErrorType(errorMessage) {
    const message = errorMessage.toLowerCase();
    
    if (message.includes('network') || message.includes('connection')) {
      return 'network';
    }
    if (message.includes('timeout')) {
      return 'timeout';
    }
    if (message.includes('too large') || message.includes('size')) {
      return 'size';
    }
    if (message.includes('type') || message.includes('format')) {
      return 'format';
    }
    if (message.includes('permission') || message.includes('unauthorized')) {
      return 'permission';
    }
    if (message.includes('quota') || message.includes('limit')) {
      return 'quota';
    }
    if (message.includes('security') || message.includes('validation')) {
      return 'security';
    }
    
    return 'general';
  }

  /**
   * Get icon for error type
   */
  function getErrorIcon(errorType) {
    switch (errorType) {
      case 'network':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
          </svg>
        );
      
      case 'timeout':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      
      case 'size':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      
      case 'format':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      
      case 'permission':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        );
      
      case 'quota':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      
      case 'security':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        );
      
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
    }
  }

  /**
   * Get color scheme for error type
   */
  function getErrorColor(errorType) {
    switch (errorType) {
      case 'network':
      case 'timeout':
        return 'orange'; // Retryable errors
      
      case 'permission':
      case 'security':
        return 'red'; // Security errors
      
      case 'quota':
        return 'yellow'; // Quota errors
      
      default:
        return 'red'; // General errors
    }
  }

  /**
   * Get user-friendly error title
   */
  function getErrorTitle(errorType, errorCount) {
    const fileText = errorCount === 1 ? 'file' : 'files';
    
    switch (errorType) {
      case 'network':
        return `Network Error (${errorCount} ${fileText})`;
      case 'timeout':
        return `Upload Timeout (${errorCount} ${fileText})`;
      case 'size':
        return `File Size Error (${errorCount} ${fileText})`;
      case 'format':
        return `File Format Error (${errorCount} ${fileText})`;
      case 'permission':
        return `Permission Error (${errorCount} ${fileText})`;
      case 'quota':
        return `Quota Exceeded (${errorCount} ${fileText})`;
      case 'security':
        return `Security Validation Failed (${errorCount} ${fileText})`;
      default:
        return `Upload Error (${errorCount} ${fileText})`;
    }
  }

  /**
   * Get user-friendly error description
   */
  function getErrorDescription(errorType) {
    switch (errorType) {
      case 'network':
        return 'Upload failed due to network connectivity issues. Please check your internet connection and try again.';
      case 'timeout':
        return 'Upload timed out. This can happen with large files or slow connections. Try uploading smaller files or check your connection.';
      case 'size':
        return 'One or more files exceed the maximum allowed size. Please use smaller files or compress them before uploading.';
      case 'format':
        return 'One or more files have an unsupported format. Please check the allowed file types and try again.';
      case 'permission':
        return 'You don\'t have permission to upload these files. Please contact your administrator if you believe this is an error.';
      case 'quota':
        return 'Upload would exceed your storage quota. Please free up space or contact your administrator to increase your quota.';
      case 'security':
        return 'Files failed security validation. This could be due to suspicious content or file corruption.';
      default:
        return 'An unexpected error occurred during upload. Please try again or contact support if the problem persists.';
    }
  }

  /**
   * Get action buttons for error type
   */
  function getActionButtons(errorGroup) {
    const buttons = [];
    
    if (errorGroup.retryable) {
      buttons.push({
        label: 'Retry',
        action: 'retry',
        primary: true
      });
    }
    
    switch (errorGroup.type) {
      case 'size':
        buttons.push({
          label: 'Size Limits',
          action: 'show_size_limits',
          primary: false
        });
        break;
      
      case 'format':
        buttons.push({
          label: 'Supported Formats',
          action: 'show_formats',
          primary: false
        });
        break;
      
      case 'quota':
        buttons.push({
          label: 'Manage Storage',
          action: 'manage_storage',
          primary: false
        });
        break;
      
      case 'permission':
        buttons.push({
          label: 'Contact Admin',
          action: 'contact_admin',
          primary: false
        });
        break;
    }
    
    return buttons;
  }

  /**
   * Handle action button click
   */
  const handleAction = (action, errorGroup) => {
    switch (action) {
      case 'retry':
        const retryableErrors = errorGroup.errors.filter(e => e.retryable);
        if (retryableErrors.length > 0) {
          onRetry(retryableErrors.map(e => e.uploadId));
        }
        break;
      
      case 'show_size_limits':
        // This would typically show a modal or tooltip with size information
        alert('File size limits:\n• PDF: 10MB\n• DOCX: 5MB\n• TXT: 1MB\n• HTML: 2MB');
        break;
      
      case 'show_formats':
        alert('Supported formats:\n• PDF documents\n• Word documents (DOCX)\n• Text files (TXT)\n• HTML files');
        break;
      
      case 'manage_storage':
        // This would typically navigate to storage management page
        console.log('Navigate to storage management');
        break;
      
      case 'contact_admin':
        // This would typically open contact form or email
        console.log('Contact administrator');
        break;
    }
  };

  if (!errors || errors.length === 0) {
    return null;
  }

  return (
    <div className={`file-upload-errors mt-4 space-y-3`} data-testid={testId}>
      {groupedErrors.map((errorGroup, groupIndex) => (
        <div
          key={`error-group-${groupIndex}`}
          className={`error-group p-4 rounded-lg border ${
            errorGroup.color === 'red'
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              : errorGroup.color === 'orange'
              ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
              : errorGroup.color === 'yellow'
              ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          }`}
        >
          <div className="flex items-start">
            <div className={`flex-shrink-0 ${
              errorGroup.color === 'red'
                ? 'text-red-400'
                : errorGroup.color === 'orange'
                ? 'text-orange-400'
                : errorGroup.color === 'yellow'
                ? 'text-yellow-400'
                : 'text-red-400'
            }`}>
              {errorGroup.icon}
            </div>
            
            <div className="ml-3 flex-1">
              <h3 className={`text-sm font-medium ${
                errorGroup.color === 'red'
                  ? 'text-red-800 dark:text-red-200'
                  : errorGroup.color === 'orange'
                  ? 'text-orange-800 dark:text-orange-200'
                  : errorGroup.color === 'yellow'
                  ? 'text-yellow-800 dark:text-yellow-200'
                  : 'text-red-800 dark:text-red-200'
              }`}>
                {getErrorTitle(errorGroup.type, errorGroup.errors.length)}
              </h3>
              
              <p className={`mt-1 text-sm ${
                errorGroup.color === 'red'
                  ? 'text-red-700 dark:text-red-300'
                  : errorGroup.color === 'orange'
                  ? 'text-orange-700 dark:text-orange-300'
                  : errorGroup.color === 'yellow'
                  ? 'text-yellow-700 dark:text-yellow-300'
                  : 'text-red-700 dark:text-red-300'
              }`}>
                {getErrorDescription(errorGroup.type)}
              </p>
              
              {/* File List */}
              {errorGroup.errors.length > 0 && (
                <div className="mt-2">
                  <details className="group">
                    <summary className={`cursor-pointer text-xs font-medium ${
                      errorGroup.color === 'red'
                        ? 'text-red-600 dark:text-red-400'
                        : errorGroup.color === 'orange'
                        ? 'text-orange-600 dark:text-orange-400'
                        : errorGroup.color === 'yellow'
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-red-600 dark:text-red-400'
                    } hover:underline`}>
                      Show affected files ({errorGroup.errors.length})
                    </summary>
                    <ul className="mt-1 space-y-1">
                      {errorGroup.errors.map((error, errorIndex) => (
                        <li 
                          key={`error-${error.id || errorIndex}`}
                          className={`text-xs ${
                            errorGroup.color === 'red'
                              ? 'text-red-600 dark:text-red-400'
                              : errorGroup.color === 'orange'
                              ? 'text-orange-600 dark:text-orange-400'
                              : errorGroup.color === 'yellow'
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          • {error.fileName}
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>
              )}
              
              {/* Action Buttons */}
              {getActionButtons(errorGroup).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {getActionButtons(errorGroup).map((button, buttonIndex) => (
                    <button
                      key={`button-${buttonIndex}`}
                      onClick={() => handleAction(button.action, errorGroup)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        button.primary
                          ? errorGroup.color === 'red'
                            ? 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-800 dark:text-red-200 dark:hover:bg-red-700'
                            : errorGroup.color === 'orange'
                            ? 'bg-orange-100 text-orange-800 hover:bg-orange-200 dark:bg-orange-800 dark:text-orange-200 dark:hover:bg-orange-700'
                            : errorGroup.color === 'yellow'
                            ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-800 dark:text-yellow-200 dark:hover:bg-yellow-700'
                            : 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-800 dark:text-red-200 dark:hover:bg-red-700'
                          : `border ${
                            errorGroup.color === 'red'
                              ? 'border-red-300 text-red-700 hover:bg-red-50 dark:border-red-600 dark:text-red-300 dark:hover:bg-red-900/50'
                              : errorGroup.color === 'orange'
                              ? 'border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-600 dark:text-orange-300 dark:hover:bg-orange-900/50'
                              : errorGroup.color === 'yellow'
                              ? 'border-yellow-300 text-yellow-700 hover:bg-yellow-50 dark:border-yellow-600 dark:text-yellow-300 dark:hover:bg-yellow-900/50'
                              : 'border-red-300 text-red-700 hover:bg-red-50 dark:border-red-600 dark:text-red-300 dark:hover:bg-red-900/50'
                          }`
                      }`}
                    >
                      {button.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="ml-3 flex-shrink-0">
              <button
                onClick={() => onDismiss(errorGroup.errors.map(e => e.id))}
                className={`${
                  errorGroup.color === 'red'
                    ? 'text-red-400 hover:text-red-600'
                    : errorGroup.color === 'orange'
                    ? 'text-orange-400 hover:text-orange-600'
                    : errorGroup.color === 'yellow'
                    ? 'text-yellow-400 hover:text-yellow-600'
                    : 'text-red-400 hover:text-red-600'
                } transition-colors`}
                aria-label="Dismiss error group"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ))}
      
      {/* Bulk Actions */}
      {errors.length > 1 && (
        <div className={`flex justify-between items-center pt-2 border-t ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {errors.length} upload error{errors.length !== 1 ? 's' : ''}
          </span>
          <div className="flex gap-2">
            {errors.some(error => error.retryable) && (
              <button
                onClick={() => onRetry(errors.filter(e => e.retryable).map(e => e.uploadId))}
                className={`text-sm font-medium transition-colors ${
                  isDark 
                    ? 'text-orange-400 hover:text-orange-300' 
                    : 'text-orange-600 hover:text-orange-700'
                }`}
              >
                Retry All
              </button>
            )}
            <button
              onClick={() => onDismiss('all')}
              className={`text-sm font-medium transition-colors ${
                isDark 
                  ? 'text-gray-400 hover:text-gray-300' 
                  : 'text-gray-600 hover:text-gray-700'
              }`}
            >
              Dismiss All
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
